/**
 * The one rule deciding whether the MCF can certify an invoice line exactly
 * as it is printed (SFE §2.4: the printed data are the data exchanged with
 * the MCF). The 31h article is the line itself: QT the quantity, PRORIG the
 * unit price when the line carries a discount, PR the net unit price, MON
 * the line total. The line is accepted only when the DGI MCF, as measured
 * (`secefItemFigures`), accepts that article and returns the SFE's total and
 * VAT.
 *
 * Refused as never measured on the device: a discount on a line carrying a
 * TSB or a specific tax, and a discount on a gross amount (unit price ×
 * quantity) that is not a whole number of centimes.
 *
 * The same rule refuses a line at entry (`mcfLineRefusal`, with the nearest
 * values it accepts) and builds the 31h article of the session plan
 * (`mcfLineItem`).
 *
 * @module @efacture/fiscal-core/services/mcf-line-rule
 */
import { secefItemFigures } from "@efacture/mcf-protocol/secef-item-rule";
import { MAX_LEVY_RAISE_PER_UNIT_CENTIMES, calculateLineTax, exactScaled, levyWithVat, } from "./tax-calculator.js";
import { creditedQuantityProposal, priceCreditProposal } from "./credit-note-line.js";
function refuse(refusal, item) {
    return item === undefined ? { ok: false, refusal } : { ok: false, refusal, item };
}
/**
 * Whether a line quantity is given to the thousandth of a unit at most: QT
 * carries three decimals, and so does every stored line quantity
 * (Decimal(15,3)). Float noise below the thousandth is not a decimal.
 */
export function quantityToTheThousandth(quantity) {
    return exactScaled(quantity, 1000) !== null;
}
/** The refusal of a line quantity finer than the thousandth, in French. */
export const QUANTITY_DECIMALS_REFUSAL = "La quantité ne peut pas avoir plus de trois décimales";
/**
 * Whether `value` carries at most `digits` decimals, at any magnitude a
 * stored amount takes: float noise below the last decimal is not a decimal
 * (`exactScaled`), and where that absolute tolerance is too tight for the
 * value's magnitude, its shortest decimal form is read.
 */
export function atMostDecimals(value, digits) {
    if (!Number.isFinite(value))
        return false;
    if (exactScaled(value, 10 ** digits) !== null)
        return true;
    const text = String(value);
    if (/e/i.test(text))
        return false;
    const point = text.indexOf(".");
    return point === -1 || text.length - point - 1 <= digits;
}
/**
 * The 31h article of a line and whether the MCF certifies it with the
 * line's amounts. `rounding` is the MCF's rounding to the centime.
 */
export function mcfLineItem(line, rounding = "half-up") {
    const quantityT = exactScaled(line.quantity, 1000);
    if (quantityT === null)
        return refuse({ reason: "QUANTITY_DECIMALS" });
    const unitH = exactScaled(line.unitPrice, 100);
    if (unitH === null)
        return refuse({ reason: "PRICE_DECIMALS" });
    const discountH = exactScaled(line.discount, 100);
    if (discountH === null)
        return refuse({ reason: "DISCOUNT_DECIMALS" });
    const amountH = exactScaled(line.totalTTC, 100);
    const totalVatH = exactScaled(line.totalTVA, 100);
    const levyH = exactScaled(line.levy, 100);
    if (amountH === null || totalVatH === null || levyH === null)
        return refuse({ reason: "AMOUNT_DECIMALS" });
    if (discountH > 0 && levyH > 0)
        return refuse({ reason: "DISCOUNT_WITH_LEVY" });
    const gross = unitH * quantityT;
    const levyWithVatH = levyH === 0 ? 0 : Math.round(levyWithVat(line.levy, line.tvaRate) * 100);
    const inPriceH = line.priceMode === "TTC" ? levyWithVatH : 0;
    const net = gross - (discountH + inPriceH) * 1000;
    if (net <= 0 || net % quantityT !== 0) {
        return refuse({
            reason: "NET_PRICE_DECIMALS",
            netAmount: net / 100_000,
            quantity: line.quantity,
            ...(levyH > 0 ? { levy: true } : {}),
        });
    }
    const unitPriceH = net / quantityT;
    const originalPriceH = discountH > 0 ? unitH : undefined;
    const figures = secefItemFigures({
        priceMode: line.priceMode,
        rate: Math.round(line.tvaRate * 10_000),
        unitPrice: unitPriceH,
        quantity: quantityT,
        amount: amountH,
        specificTax: levyH,
        ...(originalPriceH !== undefined ? { originalPrice: originalPriceH } : {}),
    }, rounding);
    const vatH = totalVatH - (levyWithVatH - levyH);
    const item = {
        quantityT,
        unitPriceH,
        ...(originalPriceH !== undefined ? { originalPriceH } : {}),
        amountH,
        levyH,
        levyWithVatH,
        vatH,
    };
    if (discountH > 0 && gross % 1000 !== 0)
        return refuse({ reason: "GROSS_FRACTION", gross: gross / 100_000 }, item);
    if (figures === null)
        return refuse({ reason: "AMOUNT", amount: amountH / 100 }, item);
    if (figures.vat !== vatH)
        return refuse({ reason: "VAT", mcfVat: figures.vat / 100, sfeVat: vatH / 100 }, item);
    return { ok: true, item };
}
/** The values and amounts of a line computed by `calculateLineTax`. */
export function mcfLineAmounts(line, calc) {
    return {
        priceMode: line.priceMode ?? "HT",
        tvaRate: calc.taxes.tvaRate,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        discount: line.discount,
        totalTTC: calc.taxes.totalTTC,
        totalTVA: calc.taxes.tvaAmount,
        levy: calc.taxes.tsbAmount + calc.taxes.specificTaxAmount,
    };
}
/** An amount with a decimal comma and two decimals. */
function money(value) {
    return value.toFixed(2).replace(".", ",");
}
/** A number with a decimal comma, as many decimals as it has. */
function plain(value) {
    return String(value).replace(".", ",");
}
/** Why the MCF cannot certify the line, in French. */
export function describeMcfLineRefusal(refusal) {
    switch (refusal.reason) {
        case "QUANTITY_DECIMALS":
            return "la quantité a plus de trois décimales";
        case "PRICE_DECIMALS":
            return "le prix unitaire a plus de deux décimales (le MCF en accepte deux)";
        case "DISCOUNT_DECIMALS":
            return "la remise a plus de deux décimales";
        case "AMOUNT_DECIMALS":
            return "un montant de la ligne n'est pas au centime";
        case "DISCOUNT_WITH_LEVY":
            return "le MCF ne reçoit pas de remise, sous une forme vérifiée, sur un article soumis à la TSB ou à une taxe spécifique";
        case "GROSS_FRACTION":
            return (`le montant brut (${plain(refusal.gross)} F) n'est pas au centime : ` +
                "le MCF ne reçoit pas de remise, sous une forme vérifiée, sur un tel montant");
        case "NET_PRICE_DECIMALS":
            return ((refusal.levy
                ? `même augmentée de ${money(MAX_LEVY_RAISE_PER_UNIT_CENTIMES / 100)} F par unité au plus (SFE §6.7), ` +
                    `la TSB ou la taxe spécifique laisse un montant hors taxe et TVA de ${money(refusal.netAmount)} F qui,`
                : `le montant net de ${money(refusal.netAmount)} F`) +
                ` divisé par la quantité ${plain(refusal.quantity)} ne donne pas un prix au centime : ` +
                "le MCF exige un prix unitaire net à deux décimales qui, multiplié par la quantité, donne exactement ce montant");
        case "AMOUNT":
            return `le MCF refuse le montant TTC de ${money(refusal.amount)} F pour ce prix et cette quantité`;
        case "VAT":
            return `le MCF calcule une TVA de ${money(refusal.mcfVat)} F au lieu de ${money(refusal.sfeVat)} F`;
    }
}
/** Candidates, in centimes, searched on each side of the entered value. */
const SUGGESTION_STEPS = 1000;
/** The line total when the MCF certifies `line` exactly, null otherwise. */
function certifiedTotal(line) {
    try {
        const calc = calculateLineTax(line);
        return mcfLineItem(mcfLineAmounts(line, calc)).ok ? calc.taxes.totalTTC : null;
    }
    catch {
        return null;
    }
}
/** The first candidate, nearest to `centimes` first, that `accepts`; lower one first on a tie. */
function nearest(centimes, min, max, accepts, includeStart) {
    for (let step = includeStart ? 0 : 1; step <= SUGGESTION_STEPS; step++) {
        for (const candidate of step === 0 ? [centimes] : [centimes - step, centimes + step]) {
            if (candidate < min || candidate > max)
                continue;
            const total = accepts(candidate);
            if (total !== null)
                return { value: candidate / 100, total };
        }
    }
    return null;
}
/**
 * The nearest new unit price below `from` whose difference, as the line's
 * unit price, credits the units to the centime and the rule accepts
 * (`priceCreditProposal`).
 */
function priceCreditSuggestion(line, from) {
    const found = priceCreditProposal(line.quantity, from, from - line.unitPrice, (difference) => certifiedTotal({ ...line, unitPrice: difference }));
    return found === null
        ? " Aucun nouveau prix unitaire proche ne la rend certifiable."
        : ` Nouveau prix unitaire accepté le plus proche : ${money(found.newPrice)} F (avoir de ${money(found.accepted)} F).`;
}
/** Whole quantities tried on each side of the entered one. */
const QUANTITY_CANDIDATES = 50;
/**
 * The nearest whole quantity, other than the entered one, the rule accepts
 * for the line as it is otherwise; the lower one first on a tie.
 */
function quantitySuggestion(line) {
    const candidates = [];
    const low = Math.max(1, Math.floor(line.quantity) - QUANTITY_CANDIDATES);
    for (let quantity = low; quantity <= Math.ceil(line.quantity) + QUANTITY_CANDIDATES; quantity++) {
        if (quantity !== line.quantity)
            candidates.push(quantity);
    }
    candidates.sort((a, b) => Math.abs(a - line.quantity) - Math.abs(b - line.quantity) || a - b);
    for (const quantity of candidates) {
        const total = certifiedTotal({ ...line, quantity });
        if (total !== null)
            return ` Quantité acceptée la plus proche : ${plain(quantity)} (montant ${money(total)} F).`;
    }
    return (" Aucune quantité proche ne la rend certifiable : " +
        (line.discount > 0
            ? "le prix de l'article est à corriger dans le catalogue, ou la remise à retirer."
            : "le prix de l'article est à corriger dans le catalogue."));
}
/**
 * The nearest quantity left to credit of the original line, other than the
 * entered one (`creditedQuantityProposal`), that the rule accepts with the
 * discount that quantity takes.
 */
function creditQuantitySuggestion(line, remainder) {
    const proposal = creditedQuantityProposal(line.quantity, remainder, (quantity, discount) => certifiedTotal({ ...line, quantity, discount }));
    if (proposal !== null) {
        return ` Quantité à créditer acceptée la plus proche : ${plain(proposal.quantity)} (montant ${money(proposal.accepted)} F).`;
    }
    return ` Aucune quantité restant à créditer sur la ligne d'origine (${plain(remainder.remainingQuantity)}) ne la rend certifiable.`;
}
/**
 * The nearest discount, and the nearest unit price without discount, the
 * rule accepts; a split of the quantity when `offerSplit`.
 */
function suggestion(line, refusal, offerSplit) {
    const grossH = Math.round(line.unitPrice * line.quantity * 100);
    const discountH = Math.round(line.discount * 100);
    const offers = [];
    if (discountH > 0) {
        const discountFixes = refusal.reason !== "DISCOUNT_WITH_LEVY" && refusal.reason !== "GROSS_FRACTION" && refusal.reason !== "PRICE_DECIMALS";
        const byDiscount = discountFixes
            ? nearest(discountH, 1, grossH - 1, (d) => certifiedTotal({ ...line, discount: d / 100 }), false)
            : null;
        if (byDiscount)
            offers.push(`remise de ${money(byDiscount.value)} F (montant ${money(byDiscount.total)} F)`);
        const netUnitH = Math.round(((line.unitPrice * line.quantity - line.discount) / line.quantity) * 100);
        const byPrice = nearest(netUnitH, 1, Number.MAX_SAFE_INTEGER, (p) => certifiedTotal({ ...line, unitPrice: p / 100, discount: 0 }), true);
        if (byPrice)
            offers.push(`prix unitaire de ${money(byPrice.value)} F sans remise (montant ${money(byPrice.total)} F)`);
    }
    else {
        const unitH = Math.round(line.unitPrice * 100);
        const byPrice = nearest(unitH, 1, Number.MAX_SAFE_INTEGER, (p) => certifiedTotal({ ...line, unitPrice: p / 100 }), unitH / 100 !== line.unitPrice);
        if (byPrice)
            offers.push(`prix unitaire de ${money(byPrice.value)} F (montant ${money(byPrice.total)} F)`);
    }
    const split = offerSplit && refusal.reason === "NET_PRICE_DECIMALS" && refusal.levy ? quantitySplit(line) : null;
    const splitText = split === null
        ? ""
        : ` Vous pouvez aussi répartir la quantité sur deux lignes, sous deux désignations différentes : ` +
            `${plain(split.first)} (montant ${money(split.totals[0])} F) et ${plain(split.second)} (montant ${money(split.totals[1])} F).`;
    if (offers.length === 0)
        return (split === null ? " Modifiez le prix, la quantité ou la remise." : "") + splitText;
    return ((offers.length === 1
        ? ` Valeur acceptée la plus proche : ${offers[0]}.`
        : ` Valeurs acceptées les plus proches : ${offers.join(" ou ")}.`) + splitText);
}
/** Whole-unit quantities tried for the first line of a split. */
const SPLIT_CANDIDATES = 50;
/**
 * The first split of the line's quantity into two lines the rule accepts
 * each: the whole units, or one unit fewer at a time, and the rest. Two
 * lines of one article and designation merge at entry (`lineMergeKey`), so
 * the two lines need different designations.
 */
function quantitySplit(line) {
    const quantityT = Math.round(line.quantity * 1000);
    let firstT = Math.floor(quantityT / 1000) * 1000;
    if (firstT === quantityT)
        firstT -= 1000;
    for (let tried = 0; firstT >= 1000 && tried < SPLIT_CANDIDATES; tried++, firstT -= 1000) {
        const first = certifiedTotal({ ...line, quantity: firstT / 1000 });
        if (first === null)
            continue;
        const second = certifiedTotal({ ...line, quantity: (quantityT - firstT) / 1000 });
        if (second !== null)
            return { first: firstT / 1000, second: (quantityT - firstT) / 1000, totals: [first, second] };
    }
    return null;
}
/**
 * The French refusal of a line the MCF cannot certify exactly as printed,
 * with the nearest values it accepts; null when it can. The line must be
 * one `calculateLineTax` accepts.
 */
export function mcfLineRefusal(line, options = {}) {
    const result = mcfLineItem(mcfLineAmounts(line, calculateLineTax(line)));
    if (result.ok)
        return null;
    const { reason } = result.refusal;
    const split = line.discount > 0 && (reason === "AMOUNT" || reason === "VAT")
        ? " (il calcule séparément la TVA du prix d'origine et celle de la remise)"
        : "";
    return (`Le MCF ne peut pas certifier cette ligne à l'identique : ${describeMcfLineRefusal(result.refusal)}${split}.` +
        (options.priceCreditFrom !== undefined
            ? priceCreditSuggestion(line, options.priceCreditFrom)
            : options.creditedLine !== undefined
                ? creditQuantitySuggestion(line, options.creditedLine)
                : options.quantityOnly === true
                    ? quantitySuggestion(line)
                    : suggestion(line, result.refusal, options.distinctDesignations ?? true)));
}
//# sourceMappingURL=mcf-line-rule.js.map